/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component } from 'react';
import { Redirect, withRouter, RouteComponentProps } from 'react-router-dom';
import { AppState } from '../app.state';
import { connect } from 'react-redux';
import { isLoggedIn, getUrlParams, wait, getMobileOperatingSystem, isNativeAppBrowser, sendNativeEvent } from '../utils';
import {
  getUserData,
  loadToken,
  loginAsGuest,
  restoreSessionFromAmplify,
} from '../authentication/authentication.actions';
import styles from './gateway.styles';
import { checkWindowSize, retrieveData, updateModels } from '../dashboard/dashboard.actions';
import { get } from 'lodash';
import {
  getEquipment,
  getEquipmentBySN,
} from '../_shared/services/manage-equipment.service';
import { Equipment, EquipmentRecord } from '../_shared/interfaces/equipment';
import { getEnv } from '../config';
import localforage from 'localforage';
import { AIcon as Icon } from '../_shared/icon';
import { REG_KEY } from '../registration';
import MobileSplash from '../mobile-splash';
import { store } from '../app.component';

interface IProps extends RouteComponentProps {
  appState: AppState;
  getUserData: () => Promise<void>;
  loadToken: () => Promise<void>;
  retrieveData: () => Promise<void>;
  updateModels: () => Promise<void>;
  checkWindowSize: () => void;
  loginAsGuest: () => void;
  restoreSessionFromAmplify: () => Promise<boolean>;
}

interface IParams {
  equipmentDetails?: string;
  org?: string;
}

export class GateWayComponent extends Component<IProps> {
  state = { thinking: true, redirect: null, mobileSplash: false, notFoundSN: null as string | null };
  initialParams: IParams = {};
  trackWidthChange = () => {
    this.props.checkWindowSize();
    window.onresize = () => {
      this.props.checkWindowSize();
    };
  };
  checkEnterAsGuest = async () => {
    let canEnter = false;
    // AER-1545: only allow guest-mode entry when the URL explicitly opts in
    // (set by the native QR-scan flow, AER-1550). A bare `equipmentDetails` or
    // `equipmentDetailsSN` URL — e.g. a push-notification link opened after
    // the session expired — should fall through to /login instead of silently
    // showing a "Guest User" session that looks half-signed-in.
    const allowGuest = get(this, 'initialParams.allowGuest');
    const hasEqParam = get(this, 'initialParams.equipmentDetailsSN') || get(this, 'initialParams.equipmentDetails');
    console.warn('[gateway] checkEnterAsGuest', { allowGuest, hasEqParam });
    if (hasEqParam && !!allowGuest && allowGuest !== 'false') {
      console.warn('[gateway] entering as guest — loginAsGuest + retrieveData');
      this.props.loginAsGuest();
      await this.props.retrieveData();
      console.warn('[gateway] retrieveData done — calling initialParamsCheck');
      await this.initialParamsCheck();
      console.warn('[gateway] initialParamsCheck done');
      this.setState({ thinking: false })
      canEnter = true;
    }

    return canEnter;
  }
  continueFlow = async () => {
    const { appState } = this.props;
    const isLogged = isLoggedIn(appState);

    this.trackWidthChange();

    const initialUrl = window.location;
    const params = getUrlParams(initialUrl.href);
    this.initialParams = params;

    console.warn('[gateway] continueFlow start', {
      href: initialUrl.href,
      pathname: initialUrl.pathname,
      search: initialUrl.search,
      isLogged: !!isLogged,
      hasAllowGuest: !!get(params, 'allowGuest'),
      hasEquipmentDetailsSN: !!get(params, 'equipmentDetailsSN'),
    });

    if (initialUrl.href.toLowerCase().includes('eqregistration')) {
      // https://aerasreg.dentalez.com/register?serialnumber=ACR2402204&activationcode=LCT9A-7D264
      // http://localhost:3000/eqRegistration?serialnumber=ACR2402204&activationcode=LCT9A-7D264 //from qr code
      // http://localhost:3000/eqRegistration?serialnumber=ACR2211181&activationcode=6WSKT-A358D //one that actually works
      // https://aerasreg.dentalez.com/eqRegistration?serialnumber=P054AF446077600&mobilesecret=2575BUJLG8GCHNP
      // http://192.168.4.199:3000/eqRegistration?serialnumber=P054AF446077600&mobilesecret=2575BUJLG8GCHNP&activationcode=6WSKT-A358D //wifi enabled
      if (params && params.serialnumber) {
        localStorage.setItem(REG_KEY, JSON.stringify({ equipment: params && params.serialnumber ? { ...params } : null }))
      }

      const os = getMobileOperatingSystem();

      if (['Android', 'iOS'].includes(os) && get(params, 'mobilesecret')) {
        return this.setState({ mobileSplash: true });
      }

      this.redirect('eqRegistration');
    } else if (initialUrl.href.includes('orgLogin') && params && params.org) {
      this.redirect(`orgLogin/${params.org}`);
    } else if (params && params.signUp) {
      localforage.clear();
      this.props.history.push('/login', {
        ...params,
      });
      this.setState({ thinking: false });
    } else if (!isLogged) {
      console.warn('[gateway] branch: not logged in → checkEnterAsGuest');
      const enteredGuest = await this.checkEnterAsGuest();
      if (!enteredGuest) {
        // AER-1668: redux had no rehydrated auth.user, but on a cold boot right
        // after login the DEZ-auth persist write can lag ~30-60s behind. Before
        // bouncing to /login, ask Amplify to restore the Cognito session; on
        // success restoreSessionFromAmplify re-seeds auth.user so we continue
        // the normal authenticated flow. On no/failed session we still redirect
        // to /login, so a genuinely logged-out user is never trapped.
        //
        // AER-1668 sign-out regression guard: logout() clears storage and
        // hard-redirects to origin + '/logout'. On THAT boot we must NOT attempt
        // the restore — the Cognito session may still be live and would
        // re-authenticate the just-signed-out user straight back to /dashboard.
        // logout() now also awaits Cognito signOut so the tokens are normally
        // already gone, but this pathname check is the deterministic belt for
        // that suspenders if signOut lags or fails. Every other pathname is a
        // normal cold boot and still attempts the restore, so the
        // reload-after-login fix (TEST 1 & TEST 2) is untouched.
        const isLogoutBoot = initialUrl.pathname === '/logout';
        if (isLogoutBoot) {
          console.warn('[gateway] logout boot — skipping amplify restore → redirect /login');
        }
        const restored = isLogoutBoot
          ? false
          : await this.props.restoreSessionFromAmplify();
        if (restored) {
          console.warn('[gateway] amplify session restored → authenticated flow');
          this.proceedAuthenticated();
        } else {
          console.warn('[gateway] no session to restore → redirect /login');
          this.redirect('/login');
        }
      }
    } else if (initialUrl.href.includes('schedule') && params && params.eq) {
      this.redirect(`/dashboard/scheduling?eq=${params.eq}`);
    } else if (initialUrl.href.toLowerCase().includes('a1Registration')) {
      this.setState({ thinking: false });
    } else {
      this.proceedAuthenticated();
    }
  };
  // AER-1668: shared "land an authenticated user" tail, used by both the normal
  // logged-in path and the new Amplify session-restore path. Refreshes user /
  // roles / token via getUserData, then redirects to the role-appropriate
  // dashboard. Role is read from the live store (not this.props.appState) so it
  // is correct even on the restore path, where the connected props may not have
  // flushed the freshly-seeded auth.user yet. Falls back to /login on failure.
  proceedAuthenticated = () => {
    localStorage.removeItem(REG_KEY);
    this.props
      .getUserData()
      .then(() => {
        const role = get(store.getState(), 'auth.user.role');

        if (role === 5) {
          this.redirect('/dashboard/manufactureProcess');
        } else {
          this.redirect('/dashboard');
        }
      })
      .catch(err => {
        console.warn(err);
        this.redirect('/login');
      });
  };
  componentDidMount = async () => {
    await this.props.loadToken().finally(this.continueFlow);
    // AER-1589: when the native shell drives in-place SPA navigation via
    // TLM.spaNavigate, react-router updates location but no route remount
    // happens for `/?equipmentDetailsSN=...` style URLs (everything is the
    // root path). Re-run initialParamsCheck on the dispatched custom event
    // so the equipment deep-link still lands on Equipment Details without
    // a full WebView reload.
    if (typeof window !== 'undefined') {
      window.addEventListener('TLM:navigate', this.onTlmNavigate);
    }
  };
  componentWillUnmount = () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('TLM:navigate', this.onTlmNavigate);
    }
  };
  onTlmNavigate = () => {
    try {
      const params = getUrlParams(window.location.href);
      this.initialParams = params;
      this.initialParamsCheck();
    } catch (err) {
      console.warn('[gateway] TLM:navigate handler failed', (err as Error).message);
    }
  };
  componentDidUpdate = (pp: IProps) => {
    const wasLogged = isLoggedIn(pp.appState);
    const isLogged = isLoggedIn(this.props.appState);
    if (isLogged && !wasLogged) {
      setTimeout(() => {
        this.initialParamsCheck();
      }, 1000);
    }
  };
  isDoneLoading = () => {
    return new Promise<void>((res) => {
      const isLoading = () => {
        const st = store.getState();
        return !!get(st, 'dash.statusesLoading', true);
      }

      const int = setInterval(() => {
        if (isLoading()) { return }

        clearInterval(int);

        res();
      }, 200);
    })
  }
  initialParamsCheck = async () => {
    const isLogged = isLoggedIn(this.props.appState);

    if (isLogged) {
      const edetailsSN = get(this, 'initialParams.equipmentDetailsSN');
      const edetails = get(this, 'initialParams.equipmentDetails');
      const redirect = get(this, 'initialParams.redirect');

      if (edetails) {
        //wait for the models to load
        await this.isDoneLoading();
        const equipment = await getEquipment(edetails);
        this.props.history.push('/dashboard/equipmentDetails', {
          equipment,
        });
      } else if (edetailsSN) {
        // AER-1589: previously this awaited isDoneLoading() (waits for the
        // full retrieveData pipeline — equipment, sensors, models, profiles,
        // integrations, etc.) before navigating. On the QR-scan flow that
        // routinely added 30-40s of staring at the loading spinner before
        // ED appeared. We now do a non-blocking cache peek + direct SN
        // endpoint lookup and push to ED immediately;
        // equipment-details.componentDidUpdate re-runs findModel as soon as
        // dash.models lands so the chair image and sensor rows hydrate
        // without blocking the initial paint.
        console.warn('[gateway] initialParamsCheck: edetailsSN =', edetailsSN);
        // Kick off updateModels in parallel so the model lookup that powers
        // the ED hero image (via findModel) is satisfied as soon as
        // possible, well before the rest of retrieveData finishes. Errors
        // are non-fatal; ED's componentDidUpdate will pick the model up
        // whenever dash.models eventually lands.
        const modelsPromise = this.props.updateModels()
          .catch(e => console.warn('[gateway] updateModels failed', (e as Error).message));
        // Non-blocking cache peek first — if the dashboard cache already
        // contains the SN (e.g. retrieveData finished while gateway was
        // initializing) skip the network round-trip.
        let equipment: Equipment | EquipmentRecord | undefined = (() => {
          const cached = get(store.getState(), 'dash.equipment', []) as Equipment[];
          return (cached || []).find((eq: Equipment) => get(eq, 'equipmentSN') === edetailsSN);
        })();
        if (equipment) {
          console.warn('[gateway] dash.equipment cache had SN, skipping API');
        }
        if (!equipment) {
          try {
            equipment = await getEquipmentBySN(edetailsSN);
            console.warn('[gateway] getEquipmentBySN returned', !!equipment);
          } catch (e) {
            console.warn('[gateway] getEquipmentBySN failed for', edetailsSN, (e as Error).message);
          }
        }
        if (!!equipment) {
          // Give the parallel models fetch a brief window to land — the call
          // is small and almost always wins this race. If it lags, fall
          // through and let ED's componentDidUpdate hydrate the model later
          // rather than blocking the navigation behind it.
          await Promise.race([modelsPromise, wait(2000)]);
          console.warn('[gateway] pushing /dashboard/equipmentDetails');
          this.props.history.push('/dashboard/equipmentDetails', {
            equipment,
          });
        } else {
          console.warn('[gateway] NO equipment found for SN, showing not-found UI');
          this.setState({ notFoundSN: edetailsSN, thinking: false });
        }
      } else if (redirect) {
        await this.isDoneLoading();
        this.props.history.push(redirect);
      }
    }
  };
  redirect = (loc: string) => {
    const env = getEnv();
    this.setState(
      {
        redirect: env === 'local' ? undefined : loc,
        thinking: false,
      },
      async () => {
        //parse any intialParams here to redirect based on url
        this.initialParamsCheck();
      }
    );
  };
  render() {
    const { redirect, thinking, mobileSplash, notFoundSN } = this.state;

    if (mobileSplash) {
      return <MobileSplash />
    }

    if (redirect) {
      //@ts-ignore
      return <Redirect to={redirect || '/'} />;
    }

    if (notFoundSN) {
      const inNative = isNativeAppBrowser();
      return (
        <div
          css={css(`
            position: fixed;
            inset: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 24px;
            background: #ffffff;
            z-index: 99999;
          `)}
        >
          <h2 css={css('margin: 0 0 12px 0; font-size: 22px; font-weight: 600;')}>
            Equipment not found
          </h2>
          <p css={css('margin: 0 0 24px 0; max-width: 360px; font-size: 15px; line-height: 1.4; color: #333;')}>
            We couldn't find an equipment record for serial number{' '}
            <strong>{notFoundSN}</strong>. The device may not be fully
            registered yet. Please sign in and register the equipment, or
            close this screen.
          </p>
          <div
            css={css(`
              display: flex;
              flex-direction: row;
              gap: 12px;
              justify-content: center;
              flex-wrap: wrap;
            `)}
          >
            {inNative && (
              <button
                css={css(`
                  padding: 10px 20px;
                  border: 1px solid #ccc;
                  background: #fff;
                  color: #333;
                  border-radius: 4px;
                  font-size: 15px;
                  cursor: pointer;
                  min-width: 120px;
                `)}
                onClick={() => sendNativeEvent({ action: 'router', data: '/' })}
              >
                Close
              </button>
            )}
            <button
              css={css(`
                padding: 10px 20px;
                border: 1px solid #6F4B9E;
                background: #6F4B9E;
                color: #fff;
                border-radius: 4px;
                font-size: 15px;
                cursor: pointer;
                min-width: 120px;
              `)}
              onClick={() => { this.props.history.push('/login'); }}
            >
              Sign in
            </button>
          </div>
        </div>
      );
    }

    return thinking ? (
      <div css={css(styles.thinkWrap)}>
        <Icon type="loading" css={css(styles.icon)} />
      </div>
    ) : null;
  }
}

export default connect(
  (appState: AppState) => ({ appState }),
  {
    getUserData,
    checkWindowSize,
    loadToken,
    loginAsGuest,
    retrieveData,
    updateModels,
    restoreSessionFromAmplify,
  }
  //@ts-ignore
)(withRouter(GateWayComponent) as any) as unknown as typeof GateWayComponent;
